import { DateTime } from 'luxon';
import * as z from 'zod';

import { Address } from '../Address';
import {
  AuthorizedContactId,
  BorrowerId,
  CompanyId,
  EntityId,
  ExternalLoanIdRecordId,
  ExternalLoanIdValue,
  LoanId,
  LoanOwnerId,
  LosId,
  PoolId,
} from '../BrandedIds';
import { zodJSPlainDate } from '../dates/BrandedDate';
import { IndexRateType } from '../indexRate';
import { OptionalPhoneNumber } from '../Phone';
import { Temporal } from '../Temporal';
import { compileServerSide, zodDateOrString } from '../utils/Zod';
import { OptionalEmailString } from '../validations/email';
import { NonNegativeInteger, NonNegativeMonetaryValue } from '../validations/fields';
import { InvestorRemittanceSplit } from './InvestorRemittanceSplit';
import { LoanFees } from './LoanFees';
import { ConstructionConfig } from './LoanSnapshot';
import { MERS } from './MERS';
import { OriginationData } from './OriginationData';
import { TaxDetails } from './TaxDetails';
import { TickEvent, TickEventType } from './TickEvent';

export interface Loan {
  id: LoanId;
  company: CompanyId;
  address: Address;
  configuration: LoanConfiguration;
  originalAmount: number;
  raw: object;
  losId: LosId;
  poolId?: PoolId;
  previousServicerId?: string;
  capitalPartnerId?: string;
  fullId?: string;
  otherId1?: string;
  otherId2?: string;
  otherId3?: string;
  ownerId?: LoanOwnerId;
  ownerLoanId?: string;
  noteflowId?: string;
  primaryBorrower: BorrowerId;
  additionalBorrowers: BorrowerId[];
  entityId?: EntityId;
  authorizedContacts?: AuthorizedContactId[];
  externalIdHistory: {
    poolId: ExternalIdHistory[];
    previousServicerId: ExternalIdHistory[];
    capitalPartnerId: ExternalIdHistory[];
    fullId: ExternalIdHistory[];
    otherId1: ExternalIdHistory[];
    otherId2: ExternalIdHistory[];
    otherId3: ExternalIdHistory[];
    ownerId: ExternalIdHistory[];
    ownerLoanId?: ExternalIdHistory[]; // LS TODO: Follow-up to make this required
  };
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date | null;
  changeId: number;
  nextTickAt?: Temporal.Instant;
  ticks?: TickEvent[];
  lastTicks?: LastTicks;
}

export type LastTicks = Partial<Record<TickEventType, Temporal.Instant>>;

export interface ExternalIdHistory {
  // Only set when the history is read back from the external_loan_ids table
  // (LoanStore.formatExternalIdHistoryByType); the in-memory snapshot copy has none.
  recordId?: ExternalLoanIdRecordId;
  startDate: Date;
  value: ExternalLoanIdValue;
  soldDate?: Date;
}

export const InterestOnlyType = z.enum(['30-360', 'actual-365', 'actual-360']);
export type InterestOnlyType = z.infer<typeof InterestOnlyType>;

export const InterestOnlyConfig = z.object({
  loanTerm: z.number(), // number of months
  type: InterestOnlyType,
});
export type InterestOnlyConfig = z.infer<typeof InterestOnlyConfig>;

export const BalloonConfig = z.object({
  amortizationTerm: z.number().int().positive(), // number of months
});
export type BalloonConfig = z.infer<typeof BalloonConfig>;

export const DrawConfiguration = z.object({
  drawsEnabled: z.boolean(),
  maxLineOfCredit: z.number(),
  drawTerm: NonNegativeInteger.optional(), // months borrower can draw down more capital
  drawExpirationDate: zodDateOrString.optional(), // date at which ability to draw is frozen
  repayBeginDate: zodDateOrString.optional(), // date at which principal payments begin and the loan starts amortizing
});
export type DrawConfiguration = z.infer<typeof DrawConfiguration>;

export const BuydownType = z.enum(['3/2/1', '2/1', '1/1', '1/0']);
export type BuydownType = z.infer<typeof BuydownType>;

export const NoteholderType = z.enum(['investor', 'servicer', 'thirdParty']);
export type NoteholderType = z.infer<typeof NoteholderType>;

export const ProductTypeEnum = z.enum([
  'fha',
  'va',
  'usda',
  'pih',
  'conv',
  'heloc',
  'bridge', // Vontive specific not a government backed loan, aka hard money
  'businessPurpose', // Vontive specific
  'jumbo',
]);
export const ProductType = ProductTypeEnum.optional();
export type ProductType = z.infer<typeof ProductType>;

// active: in active construction, I/O interest mode
// permanent: construction is done, no more draws, 30 year fixed mode
export const ConstructionToPermanentStatus = z.enum(['active', 'permanent']);
export type ConstructionToPermanentStatus = z.infer<typeof ConstructionToPermanentStatus>;

export const ConstructionLoanType = z.enum(['constructionOnly', 'constructionToPermanent']);
export type ConstructionLoanType = z.infer<typeof ConstructionLoanType>;

export const FundingChannel = z.enum(['broker', 'miniCorrespondent', 'correspondent']);
export type FundingChannel = z.infer<typeof FundingChannel>;

export const SpecialLoanStatus = z.enum(['25', '40', 'CC', 'S8']);
export type SpecialLoanStatus = z.infer<typeof SpecialLoanStatus>;

export const SpecialLoanStatusDisplayNames: Record<SpecialLoanStatus, string> = {
  '25': 'MCC 25%',
  '40': 'MCC 40%',
  CC: 'Completion Cert',
  S8: 'Section 8',
};

export const SpecialLoanStatusLongNames: Record<SpecialLoanStatus, string> = {
  '25': 'MCC with 25% credit rate',
  '40': 'MCC with 40% credit rate',
  CC: 'Completion certificate required',
  S8: 'Section 8 loan subsidy',
};

export const PreviousServicerData = z.object({
  lenderName: z.string().optional(),
  lenderPhone: OptionalPhoneNumber,
  lenderEmail: OptionalEmailString,
  purchaseDate: zodDateOrString.optional(),
  purchasedEscrowBalance: z.number().optional(),
  purchasedReserveBalance: z.number().optional(),
});
export type PreviousServicerData = z.infer<typeof PreviousServicerData>;

// WHEDA: these details are on the loan right now but should probably move elsewhere
// See: https://github.com/willowservicing/willow/issues/6043#issuecomment-2433144282
export const LoanInvestorName = z.enum(['FNMA', 'FHLMC', 'GNMA']);
export type LoanInvestorName = z.infer<typeof LoanInvestorName>;
export const isFannieFreddieInvestor = (investorName: LoanInvestorName | undefined): boolean =>
  investorName === 'FNMA' || investorName === 'FHLMC';
export const GSEName = z.enum(['FNMA', 'FHLMC', 'GNMA', 'FHA', 'MI']);
export type GSEName = z.infer<typeof GSEName>;

export const FhaAdpCode = z.enum(['703', '734']);
export type FhaAdpCode = z.infer<typeof FhaAdpCode>;

export const LoanInvestor = z.object({
  investorName: LoanInvestorName.optional(),
  investorLoanId: z.string().optional(),
  fhaCaseNumber: z.string().optional(),
  fhaAdpCode: FhaAdpCode.optional(),
  mers: MERS.optional(),
  noteholder: NoteholderType.optional(),
  documentCustodian: z.string().optional(),
});
export type LoanInvestor = z.infer<typeof LoanInvestor>;

// Number of payments per year
export const PaymentFrequency = z.union([
  z.literal(1), // Annual
  z.literal(12), // Monthly
]);
export type PaymentFrequency = z.infer<typeof PaymentFrequency>;

export const BillTiming = z.enum(['fixed', 'relative']);

export type BillTiming = z.infer<typeof BillTiming>;

const BaseLoan = z.object({
  loanTerm: z.number(), // number of months
  fundingDate: zodDateOrString,
  loanClosingDate: zodDateOrString.optional(), // Date the loan closed
  firstDueDate: zodJSPlainDate, // First payment date on the loan
  firstCollectedPaymentDate: zodJSPlainDate, // first payment collected in Willow
  incomingTransferEffectiveDate: zodDateOrString.optional(), // date the lender is responsible for servicing the loan
  minMonthlyPayment: NonNegativeMonetaryValue.optional(), // min amount that can be paid to avoid late fees
  isEscrowed: z.boolean().optional(),
  interestOnlyConfig: InterestOnlyConfig.optional(),
  balloonConfig: BalloonConfig.optional(),
  gracePeriod: z.number().optional(),
  purpose: z.enum(['purchase', 'refinance']).optional(),
  occupancyType: z.enum(['primary', 'second', 'investment']).optional(),
  legalDescription: z.string().optional(),
  /**
   * numberOfProperties: number of properties securing the mortgage (for 1098)
   * NOT the number of properties associated with the loan
   */
  numberOfProperties: z.number().optional(),
  hasInterestReserve: z.boolean(),
  reserveBalanceFundedByThirdParty: z.boolean().optional(),
  buydownType: BuydownType.optional(),
  // Original maturity date on upload, can be changed on some loans
  initialMaturityDate: zodDateOrString,
  initialDiscountPointsAmount: z.number().optional(),
  initialBeginningOfYearPrincipalBalance: z.number().optional(),
  initialYtdInterestPaid: z.number().optional(),
  initialYtdMortgageInsurancePaid: z.number().optional(),

  acquisitionDate: zodDateOrString.optional(),

  previousLoanOwner: z.string().optional(),
  previousServicer: PreviousServicerData.optional(),
  brokerName: z.string().optional(),

  fees: LoanFees.optional(),

  investor: LoanInvestor.optional(),
  lienPosition: NonNegativeInteger.optional(), // order of claim priority in the event of default/foreclosure
  noteDate: zodDateOrString.optional(), // date the borrower and original lender signed at closing (most of the time is the same as the loan closing date)
  qualifiedMortgage: z.boolean().optional(), // indicates that a borrower has been deemed able to meet their mortgage obligations
  paymentFrequency: PaymentFrequency,
  billTiming: BillTiming.optional(), // Make required once all loans have been updated
  taxDetails: TaxDetails.optional(),
  delinquentInterestRate: z.number().optional(), // decimal version
  delinquentInterestRemittanceSplit: InvestorRemittanceSplit.optional(),
  originalNoteHolder: z.string().optional(),
  servicerOrgId: z.string().optional(),
  subServicerOrgId: z.string().optional(),
  investorOrgId: z.string().optional(),
  ppc1OrgId: z.string().optional(),
  originationData: OriginationData.optional(),
  mipLTVThresholdDate: zodDateOrString.optional(),
  isAssumable: z.boolean().optional(),
  fundingChannel: FundingChannel.optional(),
  completionCertFlag: z.boolean().optional(),
  completionCertNotes: z.string().optional(),
});

export const LoanConstructionConfig = z.object({
  constructionLoanType: ConstructionLoanType,
  dutch: z.boolean(), // true = dutch (accrue interest on the max line of credit), false = non-dutch (accrue interest on the drawn principal)
  fixedPeriodPrincipalTotal: z.number().optional(),
});

export type LoanConstructionConfig = z.infer<typeof LoanConstructionConfig>;

export const FixedLoanProductType = z.enum(['fha', 'va', 'usda', 'pih', 'conv', 'bridge', 'businessPurpose', 'jumbo']);

export type FixedLoanProductType = z.infer<typeof FixedLoanProductType>;

export const FixedLoan = BaseLoan.extend({
  paymentType: z.literal('fixed'),
  productType: FixedLoanProductType.optional(),
  annualInterestRate: z.number(), // represented as 0.035 for 3.5%
  constructionConfig: LoanConstructionConfig.optional(),
  drawConfiguration: DrawConfiguration.optional(),
});
export type FixedLoan = z.infer<typeof FixedLoan>;

export const FixedConstructionLoan = FixedLoan.required({
  constructionConfig: true,
  drawConfiguration: true,
});
export type FixedConstructionLoan = z.infer<typeof FixedConstructionLoan>;

export const ArmRoundingType = z.enum(['up', 'down', 'nearest']);
export type ArmRoundingType = z.infer<typeof ArmRoundingType>;

export const ArmLoan = BaseLoan.extend({
  paymentType: z.literal('arm'),
  productType: z.enum(['fha', 'va', 'usda', 'pih', 'conv', 'bridge', 'businessPurpose', 'jumbo']).optional(),
  fixedPeriodConfig: z.object({
    annualInterestRate: z.number(), // this captures the initial interest rate
    loanTerm: z.number().optional(), // number of months
  }),
  annualInterestRate: z.number(), // original interest rate
  armIndex: IndexRateType.optional(),
  margin: z.number().optional(), // 50 bps should be represented as 0.005
  maxIncreasePerPeriod: z.number().optional(), // 50 bps should be represented as 0.005
  minIncreasePerPeriod: z.number().optional(), // 50 bps should be represented as 0.005
  maxIncreaseForFirstPeriod: z.number().optional(), // 50 bps should be represented as 0.005
  minIncreaseForFirstPeriod: z.number().optional(), // 50 bps should be represented as 0.005
  rateCeiling: z.number().optional(), // 5% should be represented as 0.05
  rateFloor: z.number().optional(), // 5% should be represented as 0.05
  rateAdjustmentFrequency: z.number().optional(), // in months
  adjustmentLeadCountDays: z.number().optional(),
  roundingType: ArmRoundingType.optional(),
  // TODO: we actually have been storing this as a decimal... sigh. This should be relabeled
  roundingPercent: z.number().optional(),
  constructionConfig: LoanConstructionConfig.optional(),
  drawConfiguration: DrawConfiguration.optional(),
});
export type ArmLoan = z.infer<typeof ArmLoan>;

export const HelocLoan = BaseLoan.extend({
  paymentType: z.enum(['fixed']),
  productType: z.literal('heloc'),
  drawConfiguration: DrawConfiguration,
  annualInterestRate: z.number(), // represented as 0.035 for 3.5%
});
export type HelocLoan = z.infer<typeof HelocLoan>;

export const HelocArmLoan = ArmLoan.extend({
  productType: z.literal('heloc'),
  drawConfiguration: DrawConfiguration,
});
export type HelocArmLoan = z.infer<typeof HelocArmLoan>;

export const ArmConstructionLoan = ArmLoan.extend({
  constructionConfig: LoanConstructionConfig,
  drawConfiguration: DrawConfiguration,
});
export type ArmConstructionLoan = z.infer<typeof ArmConstructionLoan>;

export const ArmEnabledLoanConfig = z.union([ArmLoan, HelocArmLoan]);
export type ArmEnabledLoanConfig = z.infer<typeof ArmEnabledLoanConfig>;

export const LoanConfiguration = compileServerSide(
  z.union([FixedLoan, ArmLoan, HelocLoan, HelocArmLoan, FixedConstructionLoan, ArmConstructionLoan]),
);
export type LoanConfiguration = z.infer<typeof LoanConfiguration>;

export const AmortizationType = z.enum(['fixed', 'arm']);
export type AmortizationType = z.infer<typeof AmortizationType>;

export const isHelocLoan = (productType: ProductType) => productType === 'heloc';

export const isHelocLoanConfig = (loanConfiguration: Loan['configuration']): loanConfiguration is HelocLoan =>
  loanConfiguration.productType === 'heloc';

export const isConstructionLoan = (constructionLoanType: ConstructionLoanType | undefined): boolean =>
  constructionLoanType === 'constructionOnly' || constructionLoanType === 'constructionToPermanent';

export const isActiveConstructionLoan = (
  constructionLoanType: ConstructionLoanType | undefined,
  constructionToPermanentStatus: ConstructionToPermanentStatus | undefined,
): boolean =>
  constructionLoanType === 'constructionOnly' ||
  (constructionLoanType === 'constructionToPermanent' && constructionToPermanentStatus === 'active');

export const isPermanentConstructionLoan = (
  constructionLoanType: ConstructionLoanType | undefined,
  effectiveDate: Date | undefined,
  targetDate: Date,
): boolean =>
  constructionLoanType === 'constructionToPermanent' &&
  !!effectiveDate &&
  DateTime.fromJSDate(effectiveDate).startOf('day').toJSDate() <=
    DateTime.fromJSDate(targetDate).startOf('day').toJSDate();

/* This returns true if the loan has draw configuration, and does not necessarily have to be enabled at this moment.
   This is useful for things like displaying past draws on the frontend.
*/
export const isDrawableLoanConfig = (loanConfiguration: Loan['configuration']): boolean =>
  isHelocLoanConfig(loanConfiguration) ||
  isConstructionLoanConfig(loanConfiguration) ||
  (loanConfiguration.drawConfiguration?.maxLineOfCredit ?? 0) > 0;

export const isConstructionLoanConfig = (
  loanConfiguration: Loan['configuration'],
): loanConfiguration is FixedConstructionLoan =>
  'constructionConfig' in loanConfiguration && !!loanConfiguration.constructionConfig;

export const isArmEnabledLoanConfig = (
  loanConfiguration: Loan['configuration'],
): loanConfiguration is ArmEnabledLoanConfig => loanConfiguration.paymentType === 'arm';

export const isNonConvertedArmEnabledLoanConfig = (
  loanConfiguration: Loan['configuration'],
  constructionConfig: ConstructionConfig | undefined,
  targetDate: Date,
): boolean =>
  loanConfiguration.paymentType === 'arm' &&
  !(
    constructionConfig &&
    isPermanentConstructionLoan(
      loanConfiguration.constructionConfig?.constructionLoanType,
      constructionConfig.constructionModificationEffectiveDate,
      targetDate,
    )
  );

export const isFixedLoanConfig = (loanConfiguration: LoanConfiguration): loanConfiguration is FixedLoan =>
  loanConfiguration.paymentType === 'fixed';

export const isDutch = (loanConfiguration: Loan['configuration']): boolean =>
  isConstructionLoanConfig(loanConfiguration) && loanConfiguration.constructionConfig.dutch;

export const isInterestOnly = (loanConfiguration: Loan['configuration']): boolean =>
  !!loanConfiguration.interestOnlyConfig;

export const isBalloonLoan = (loanConfiguration: Loan['configuration']): boolean => !!loanConfiguration.balloonConfig;

/**
 * The term payments amortize over. For balloon loans this exceeds `loanTerm`
 * (the term to maturity), leaving the unamortized principal due at maturity.
 */
export const getAmortizationTermMonths = (loanConfiguration: Loan['configuration']): number =>
  loanConfiguration.balloonConfig?.amortizationTerm ?? loanConfiguration.loanTerm;

/* Infers billing timing based on the first payment date and if it is a construction or heloc loan */
export const getBillTiming = (firstPaymentDate: Date, isFixedConstructionOrHeloc: boolean): BillTiming => {
  const dueDay = DateTime.fromJSDate(firstPaymentDate).day;

  if (isFixedConstructionOrHeloc) {
    if (dueDay === 25 || dueDay === 1) {
      return 'fixed';
    }
  }
  if (dueDay === 1) {
    return 'fixed';
  }
  return 'relative';
};
